import React, { useEffect } from 'react';
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import {
  autoPlacement,
  autoUpdate,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/solid';

import { useTranslation } from 'react-i18next';

/**
 * Properties for the MultiSelectWithSuggestions component.
 * Allows selection of multiple items from a list of suggestions, with support for free-form entry.
 */
export interface MultiSelectWithSuggestionsProps {
  /**
   * List of suggestions to display. Each suggestion can be a string or an object with label and value.
   */
  suggestions: (string | { label: string; value: string })[];
  /**
   * Currently selected values. Each value can be a string or an object with label and value.
   */
  value: (string | { label: string; value: string })[];
  /**
   * Callback when the selection changes.
   * @param newValues The updated array of selected values.
   */
  onChange: (newValues: (string | { label: string; value: string })[]) => void;
  /**
   * Optional callback when the input text changes.
   * @param text The current input text.
   */
  onInputTextChange?: (text: string) => void;
  /**
   * Placeholder text for the input field.
   * @default 'Type to search...'
   */
  placeholder?: string;
  /**
   * Maximum number of tags that can be selected.
   * @default 200
   */
  maxTags?: number;
  /**
   * Whether the component is disabled.
   * @default false
   */
  disabled?: boolean;
  /**
   * Whether the component is in a loading state.
   */
  loading?: boolean;
  /**
   * Additional CSS classes for the input.
   */
  className?: string;
  /**
   * Error message or object to display.
   */
  error?: string | Error;
  /**
   * Optional function to validate free-form input. Returns error string if invalid, or null if valid.
   */
  freeFormValidator?: (input: string) => string | null;
}

/**
 * Helper to extract a unique key from a suggestion item.
 * @param item The suggestion item (string or object).
 * @returns The key string for comparison.
 */
const getItemKey = (item: string | { label: string; value: string }): string =>
  typeof item === 'string' ? item : item.value;

/**
 * MultiSelectWithSuggestions component allows users to select multiple items from suggestions,
 * with support for free-form entry and pagination of suggestions.
 *
 * @param suggestions List of suggestions to display.
 * @param value Currently selected values.
 * @param onChange Callback when the selection changes.
 * @param onInputTextChange Optional callback when the input text changes.
 * @param placeholder Placeholder text for the input field.
 * @param maxTags Maximum number of tags that can be selected.
 * @param disabled Whether the component is disabled.
 * @param className Additional CSS classes for the input.
 * @param loading Whether the component is in a loading state.
 * @param error Error message or object to display.
 */
export function MultiSelectWithSuggestions({
  suggestions,
  value,
  onChange,
  onInputTextChange,
  placeholder = 'Type to search...',
  maxTags = 200,
  disabled = false,
  className,
  loading,
  error,
  freeFormValidator,
}: MultiSelectWithSuggestionsProps) {
  const { t } = useTranslation('designSystem');
  const [query, setQuery] = React.useState('');
  const PAGE_SIZE = 100;
  const [page, setPage] = React.useState(1);
  const isAtTagsLimit = value.length >= maxTags;
  const [freeFormError, setFreeFormError] = React.useState<string | null>(null);

  const normalizedQuery = query.toLowerCase().replace(/\s+/g, '');
  const filtered = suggestions.filter((s) => {
    const suggestionText = typeof s === 'string' ? s : s.label;
    return suggestionText
      .toLowerCase()
      .replace(/\s+/g, '')
      .includes(normalizedQuery);
  });
  const paginatedSuggestions = filtered.slice(0, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [query]);

  // Setup floating UI
  const { x, y, refs, context } = useFloating({
    placement: 'bottom-start',
    middleware: [
      offset(5),
      shift({ padding: 8 }),
      autoPlacement({ allowedPlacements: ['bottom-start'] }), // add 'top-start' for top placement
    ],
    whileElementsMounted: autoUpdate,
    strategy: 'fixed',
  });

  const { getFloatingProps } = useInteractions([
    useRole(context, { role: 'listbox' }),
    useDismiss(context),
  ]);

  function addValue(newItem: string | { label: string; value: string }) {
    if (value.length >= maxTags) {
      return;
    }
    if (value.some((v) => getItemKey(v) === getItemKey(newItem))) {
      return;
    }
    onChange([...value, newItem]);
  }

  function removeValue(item: string | { label: string; value: string }) {
    onChange(value.filter((v) => getItemKey(v) !== getItemKey(item)));
  }

  function handleFreeForm() {
    const trimmed = query.trim();
    if (freeFormValidator) {
      const error = freeFormValidator(trimmed);
      if (error) {
        setFreeFormError(error);
        return;
      }
    }
    if (trimmed && !value.some((v) => getItemKey(v) === trimmed)) {
      addValue(trimmed);
      setFreeFormError(null);
    }
  }

  function renderSelectedSuggestions() {
    return (
      <>
        {value.map((item) => (
          <div
            key={getItemKey(item)}
            className="border-action-primary flex items-center gap-2 rounded-md border bg-transparent px-2 py-1 text-sm text-black dark:text-white ring-0 focus:ring-0 focus:outline-none"
          >
            <span>{typeof item === 'string' ? item : item.label}</span>
            {!disabled && (
              <XMarkIcon
                className="text-action-primary hover:text-action-primary/80 h-4 w-4 cursor-pointer"
                onClick={() => removeValue(item)}
              />
            )}
          </div>
        ))}
      </>
    );
  }

  return (
    <div className="relative inline-block w-auto flex-grow-0">
      {disabled ? (
        <div className="flex flex-wrap gap-2">
          {renderSelectedSuggestions()}
        </div>
      ) : (
        <>
          <Combobox
            value={value}
            onChange={(
              val:
                | string
                | { label: string; value: string }
                | (string | { label: string; value: string })[],
            ) => {
              if (Array.isArray(val)) {
                onChange(val);
                setQuery('');
              } else {
                // Prevent adding if already selected
                if (!value.some((v) => getItemKey(v) === getItemKey(val))) {
                  addValue(val);
                }
                setQuery('');
              }
            }}
            multiple
            disabled={disabled}
          >
            {({ open, activeOption }) => (
              <>
                <div ref={refs.setReference}>
                  <ComboboxInput
                    className={`focus:!border-br-primary focus:ring-br-primary w-[500px] rounded-md border-gray-300 dark:border-gray-600 p-2 text-sm placeholder-gray-400 dark:placeholder-gray-500 shadow-sm focus:!outline-none ${className}`}
                    placeholder={placeholder}
                    disabled={isAtTagsLimit}
                    displayValue={() => ''}
                    onChange={(e) => {
                      const text = e.target.value;
                      setQuery(text);
                      onInputTextChange && onInputTextChange(text);
                      setFreeFormError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !activeOption) {
                        handleFreeForm();
                        setQuery('');
                      }
                    }}
                  />
                </div>

                {open && (
                  <div
                    className="z-[9999999999] mt-1 w-[450px] rounded-md bg-white dark:bg-gray-900 p-2 shadow-lg ring-1 ring-black/5 dark:ring-gray-600"
                    {...getFloatingProps({
                      ref: refs.setFloating,
                      style: {
                        position: 'fixed',
                        top: y ?? '',
                        left: x ?? '',
                      },
                    })}
                  >
                    <ComboboxOptions>
                      {loading && (
                        <div className="p-2 text-sm text-gray-500 dark:text-gray-400">
                          {t('common:loadingSuggestions')}
                        </div>
                      )}
                      {error && (
                        <div className="p-2 text-sm text-red-500">
                          {t('common:errorLoadingSuggestions')}
                        </div>
                      )}

                      {!loading &&
                        !error &&
                        paginatedSuggestions.length < 1 && (
                          <div className="p-2 text-sm text-gray-500 dark:text-gray-400">
                            {t('noMatchesFoundPressEnterToAdd', { query })}
                          </div>
                        )}

                      {!loading &&
                        !error &&
                        paginatedSuggestions.map((suggestion) => {
                          const isSelected = value.some(
                            (v) => getItemKey(v) === getItemKey(suggestion),
                          );
                          return (
                            <ComboboxOption
                              key={
                                typeof suggestion === 'string'
                                  ? suggestion
                                  : suggestion.value
                              }
                              value={suggestion}
                              disabled={isSelected}
                              className={({ focus, disabled }) =>
                                `relative flex cursor-pointer items-center justify-between px-3 py-2 text-sm ${
                                  focus
                                    ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100'
                                    : 'text-gray-700 dark:text-gray-300'
                                } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`
                              }
                              onClick={() => {
                                if (!isSelected) {
                                  setQuery('');
                                }
                              }}
                            >
                              <span>
                                {typeof suggestion === 'string'
                                  ? suggestion
                                  : suggestion.label}
                              </span>
                              {isSelected && (
                                <CheckIcon
                                  className="text-action-primary ml-2 h-4 w-4"
                                  aria-hidden="true"
                                />
                              )}
                            </ComboboxOption>
                          );
                        })}
                    </ComboboxOptions>
                  </div>
                )}
              </>
            )}
          </Combobox>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {renderSelectedSuggestions()}
          </div>
          {freeFormError && (
            <div className="mt-1 text-sm text-red-500">{freeFormError}</div>
          )}
        </>
      )}
    </div>
  );
}
